import { EventEmitter, h, Component, Prop, Element, Event, State, Watch } from '@stencil/core';

@Component({
  tag: 'pk-tab',
  styleUrl: './pk-tab.scss',
  shadow: true,
})
export class PkTab {
  // Own Properties
  // ====================

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkTabElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() _selected: boolean;

  // Public Property API
  // ========================

  /** Set the state disabled for tab */
  @Prop({ reflect: true }) disabled: boolean;

  /** Set the state selected for tab */
  @Prop({ reflect: true }) selected: boolean = false;

  /** Set the tab id */
  @Prop({ reflect: true }) tabId: string;

  // Prop lifecycle events
  // =======================

  @Watch('selected')
  handleSelectedPropChange() {
    this._selected = this.selected;
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Callback handler to be called when the tab gets clicked. */
  @Event() pkTabButtonClick: EventEmitter<{ tabId: string }>;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentWillLoad() {
    this.handleSelectedPropChange();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private handleClick = () => {
    if (this.disabled) return;

    this.pkTabButtonClick.emit({ tabId: this.tabId });
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    return (
      <button
        id={this.tabId}
        aria-disabled={this.disabled}
        aria-selected={this._selected}
        class={{ 'pk-tab__button': true, active: this._selected }}
        disabled={this.disabled}
        onClick={this.handleClick}
        role="tab"
        type="button"
        part="tab"
      >
        <div part="icon">
          <slot name="icon" />
        </div>
        <slot />
      </button>
    );
  }
}
